import React, { useState, useEffect } from 'react';
import Rater from 'react-rater';
import { shallowEqual, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { get } from 'lodash';
import { RootState } from 'modules';
import { DisplayName } from 'components/base/DisplayName';
import { ProfileWrapper } from 'components/mentoring/styles';
import { setInterestedMentor } from 'libs/api/user';
import { TPagination } from 'modules/types';
import { TSimpleMentor, TMentor } from 'modules/user';
import { CUserType, CUserTypeColor } from 'constants/user.const';
import { EUserType } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Avatar, Tag, Col, Row, Divider, Button } from 'antd';
import { LikeFilled, RightOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { PrimaryColor, SecondaryColor, LightGrayColor } from 'GlobalStyles';
import CheckedSource from 'assets/icons/FavoriteChecked.webp';
import UncheckedSource from 'assets/icons/FavoriteUnchecked.webp';

export const CardWrapper = styled.div`
  color: #000;
  max-width: 280px;
  width: 100%;
  overflow: hidden;
  display: block;
  position: relative;
  text-align: center;
  border: 1px solid #f0f8f6;
  border-radius: 8px;
  padding-top: 15px;
  font-size: 13px;
  background: #fff;
  .card-detail {
    width: 100%;
    background: ${LightGrayColor};
    color: ${PrimaryColor};
    border-radius: 0 0 8 8px;
    margin-top: 8px;
    padding: 10px;
    text-align: left;
  }
  .name {
    margin-top: -8px;
  }
  .intro {
    line-height: 18px;
    color: #666666;
  }
  .score-wrapper {
    margin-top: -4px;
  }
  .ant-divider {
    border-color: #f0f8f6;
  }
  .card-detail:hover {
    font-weight: bold;
  }
`;
type TMentorCardProps = {
  mentor: TSimpleMentor | TMentor;
  pagination?: TPagination;
  reloadPage?: any;
  maxWidth?: number;
  isCurrentUser?: boolean;
  style?: object;
  userTagColor?: string;
};
const MentorCard = ({
  mentor,
  pagination,
  reloadPage,
  maxWidth = 300,
  isCurrentUser = false,
  style,
  userTagColor,
}: TMentorCardProps) => {
  const [isInterested, setIsInterested] = useState(get(mentor, 'is_interested', false));
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const showInterested = currentUser && get(currentUser, 'user_type') === EUserType.Mentee ? true : false;
  const tagColor = userTagColor
    ? userTagColor
    : get(mentor, 'mentor_type') === 'senior'
    ? `${PrimaryColor}`
    : `${SecondaryColor}`;

  async function handleInterested() {
    try {
      await setInterestedMentor({
        mentor_id: get(mentor, 'id'),
        is_interested: !isInterested,
      });
      setIsInterested(!isInterested);
      reloadPage && (await reloadPage());
    } catch (e) {
      throw e;
    }
  }

  useEffect(() => {
    setIsInterested(get(mentor, 'is_interested', false));
  }, [pagination, mentor]);

  const tags = []
  const mentoring_tags = get(mentor, 'mentoring_tags', [])
  const mentoring_tags_length = mentoring_tags.length > 2 ? 2 : mentoring_tags.length
  for (let index = 0; index < mentoring_tags_length; index++) {
    tags.push(
      <Col style={{ width: '100%', marginTop: '5px' }}>
        <Button
          size='small'
          type='link'
          style={{ width: '80%', fontSize: '13px', marginLeft: '20px', marginRight: '10px' }}
          icon={<LikeFilled />}
          >
            { mentoring_tags[index] }
        </Button>
      </Col>
    );
  }

  return (
    <CardWrapper style={{ ...style, maxWidth: `${maxWidth}px` }}>
      {isCurrentUser === false && showInterested === true && (
        <div onClick={handleInterested} className='favorite-btn'>
          {isInterested === true ? (
            <img alt='checked' src={CheckedSource} />
          ) : (
            <img alt='unchecked' src={UncheckedSource} />
          )}
        </div>
      )}
      <ProfileWrapper style={{ maxWidth: '180px' }}>
        {isCurrentUser === true ? (
          <Link to={`${ERoute.UserEdit}`}>
            <Avatar src={get(mentor, 'user.profile_image.absolute_url')} />
          </Link>
        ) : (
          <Link
            target='_blank'
            to={`${ERoute.MentoringMentorView.replace(':record_id', String(get(mentor, 'id')))}`}
          >
            <Avatar src={get(mentor, 'user.profile_image.absolute_url')} />
          </Link>
        )}
        {isCurrentUser === true ? (
          <Tag className='user-type-tag' color={CUserTypeColor[EUserType.Mentor]}>
            {CUserType[EUserType.Mentor]}
          </Tag>
        ) : (
          <Tag className='user-type-tag' color={tagColor}>
            {/* {get(mentor, "is_current") ? "현직" : "전직"} */}
            {get(mentor, 'mentor_type') === 'junior' ? '주니어' : '시니어'}
          </Tag>
        )}
        {isCurrentUser === true ? (
          <>
            {get(mentor, 'user.use_nickname') === true ? (
              <div className='name'>
                <Link to={`${ERoute.UserEdit}`} style={{ color: '#000' }}>
                  {get(mentor, 'user.nickname')}
                </Link>
              </div>
            ) : (
              <div className='name'>
                <Link to={`${ERoute.UserEdit}`} style={{ color: '#000' }}>
                  {get(mentor, 'user.fullname')}
                </Link>
              </div>
            )}
          </>
        ) : (
          <div className='name'>
            <Link
              target='_blank'
              to={`${ERoute.MentoringMentorView.replace(':record_id', String(get(mentor, 'id')))}`}
              style={{ color: '#000' }}
            >
              {get(mentor, 'user.fullname')}
            </Link>
          </div>
        )}
        <div className='intro'>"{get(mentor, 'short_introduction')}"</div>
        <div className='score' style={{ marginTop: '5px' }}>
          멘토링 횟수 {get(mentor, 'mentoring_count', 0)}회<br />
          리뷰 수 {get(mentor, 'num_of_rater', 0)}개
        </div>
        { currentUser?.user_type === EUserType.Mentee ?
          <Row justify='center' align='middle'>
            <Col style={{ marginRight: '5px' }}>
              <Rater interactive={false} rating={get(mentor, 'score_average')} />
            </Col>
            <Col style={{ marginTop: '8px' }}>
              <div className='score-wrapper'>
                <span className='score'>{get(mentor, 'score_average')}</span>
              </div>
            </Col>
          </Row>
          :
          <Row justify='center' align='middle'>
            <Col>
              <div className='score' style={{ marginTop: '5px' }}>
              </div>
            </Col>
          </Row>
        }
      </ProfileWrapper>
      { tags }
      <Divider />
      <Row justify='space-between' style={{ margin: '0px 7px' }}>
        {get(mentor, 'user.primary_work_experience') ? (
          <>
            <div>{DisplayName(get(mentor, 'user.primary_work_experience.company_name'))}</div>
            <div>{get(mentor, 'user.primary_work_experience.duration_display')}</div>
          </>
        ) : (
          <div>&nbsp;</div>
        )}
      </Row>
      <div style={{ margin: '0px 7px', textAlign: 'left' }}>
        {get(mentor, 'user.educations', []).length > 0 ? (
          <div style={{ display: 'flex' }}>
            <div className='ellipsis-line-1' style={{ maxWidth: '87%', wordBreak: 'break-all' }}>
              {DisplayName(get(mentor, 'user.educations', [])?.slice(-1)[0]?.university_name)}{' '}
              {get(mentor, 'user.educations', [])?.slice(-1)[0]?.major}
            </div>
            <div style={{ marginLeft: '2px' }}>졸업</div>
          </div>
        ) : (
          <div>&nbsp;</div>
        )}
      </div>
      {isCurrentUser === true ? (
        <Link to={`${ERoute.UserEdit}`}>
          <Row className='card-detail' justify='space-between'>
            <Col>내 정보 수정하기</Col>
            <Col>
              <RightOutlined />
            </Col>
          </Row>
        </Link>
      ) : (
        <Link
          target='_blank'
          to={`${ERoute.MentoringMentorView.replace(':record_id', String(get(mentor, 'id')))}`}
        >
          <Row className='card-detail' justify='space-between' style={{ backgroundColor: '#ffffff' }}>
            <Col>{get(mentor, 'user.fullname')} 멘토 상세정보 보기</Col>
            <Col>
              <RightOutlined />
            </Col>
          </Row>
        </Link>
      )}
    </CardWrapper>
  );
};

export default MentorCard;
